import classNames from 'classnames'
import { Fragment, h, JSX } from 'preact'

import { Button, ButtonProps } from '../../components/button/button'
import { MinusIcon, PlusIcon } from '../../components/icon/icon'
import { Image, ImageProps } from '../../components/image/image'
import { Loader } from '../../components/loader/loader'
import { PaymentError, type PaymentErrorProps } from '../../components/payment-error/payment-error'
import {
    ProductsSidebar,
    ProductsSidebarProps,
} from '../../components/products-sidebar/products-sidebar'
import { ProgressBar, ProgressBarProps } from '../../components/progress-bar/progress-bar'
import { Timer, TimerProps } from '../../components/timer/timer'
import { CheckoutStep, PluginTheme } from '../../types'
import { CardsSection, CardsSectionProps } from './cards-section'
import { ContactSection, ContactSectionProps } from './contact-section'
import { EventSelectionSection, EventSelectionSectionProps } from './event-selection-section'
import { FloorplanSection, FloorplanSectionProps } from './floorplan-section'
import { IntroSection, IntroSectionProps } from './intro-section'
import { PaymentSection, PaymentSectionProps } from './payment-section'
import { ProductFiltersSection, ProductFiltersSectionProps } from './product-filters-section'
import { ProductsListSection, ProductsListSectionProps } from './products-list-section'
import { ProgressBarSection } from './progress-bar-section'
import { TicketsSection, TicketsSectionProps } from './tickets-section'

export interface SelectedSeatItem {
    text: string
}

export interface SelectedSeatProps {
    title: string
    changeLink?: ButtonProps
    items: SelectedSeatItem[]
    disableToggle?: boolean
    onToggle: () => void
    isOpen?: boolean
}

export interface CheckoutProps {
    isFree?: boolean
    theme: PluginTheme
    activeCheckoutStep?: CheckoutStep
    interval?: unknown
    progressBar?: ProgressBarProps
    timer?: TimerProps
    productsSidebar?: ProductsSidebarProps
    production: {
        image: ImageProps
        name: string
    }
    dateTime: {
        date: string
        time: string
    }
    venueName?: string
    additionalInfo?: string
    selectedSeats?: SelectedSeatProps
    hasFloorplan?: boolean
    eventSelectionSection?: EventSelectionSectionProps
    floorplanSection?: FloorplanSectionProps
    introSection?: IntroSectionProps
    productFiltersSection?: ProductFiltersSectionProps
    productsListSection?: ProductsListSectionProps
    ticketsSection?: TicketsSectionProps
    cardsSection?: CardsSectionProps
    contactSection?: ContactSectionProps
    paymentSection?: PaymentSectionProps
    text?: string
    isLoading?: boolean
    terms?: {
        label: string
        link: string
    }
    error?: PaymentErrorProps
    buttons: ButtonProps[]
    isProductSaleEnabled: boolean
    productSelectionText: string
    paySectionText: string
}

const renderMobileHeader = (props: CheckoutProps): JSX.Element => (
    <>
        {props.progressBar && <ProgressBar {...props.progressBar} mode="mobile" />}
        {props.timer && <Timer {...props.timer} modifier="timer--mobile" />}
    </>
)

const renderSidebar = (props: CheckoutProps): JSX.Element => (
    <div class="checkout__sidebar">
        {props.productsSidebar ? (
            <ProductsSidebar {...props.productsSidebar} />
        ) : (
            renderTicketsSidebar(props)
        )}
    </div>
)

const renderTicketsSidebar = (props: CheckoutProps): JSX.Element => (
    <>
        {props.timer && (
            <Timer class="checkout__timer" {...props.timer} modifier="timer--desktop" />
        )}
        <div className="checkout__film">
            <Image
                modifier="image--fluid"
                class="checkout__poster"
                {...props.production.image}
                sizes="240px"
            />
            <div className="checkout__session">
                <h1 className="checkout__title">{props.production.name}</h1>
                {props.dateTime.date && (
                    <span className="checkout__date">{props.dateTime.date}</span>
                )}
                {props.dateTime.time && (
                    <span className="checkout__time">{props.dateTime.time}</span>
                )}
                {props.venueName && <span className="checkout__venue-name">{props.venueName}</span>}
            </div>
            {props.additionalInfo && <div className="checkout__info">{props.additionalInfo}</div>}
            {props.selectedSeats && <SelectedSeats {...props.selectedSeats} />}
        </div>
    </>
)

const SelectedSeats = (props: SelectedSeatProps): JSX.Element => (
    <div className={classNames('checkout__selected-tickets', { 'is-open': props.isOpen })}>
        {!props.disableToggle && (
            <div className="selected-tickets__label" onClick={props.onToggle}>
                {props.isOpen ? (
                    <MinusIcon class="selected-tickets__toggle" />
                ) : (
                    <PlusIcon class="selected-tickets__toggle" />
                )}
                {props.title}
            </div>
        )}
        <div className="selected-tickets__items">
            {props.items.map((seat, index) => (
                <div key={index} className="selected-tickets__item">
                    <span className="selected-tickets__seat">{seat.text}</span>
                </div>
            ))}
            {props.changeLink && <Button class="selected-tickets__link" {...props.changeLink} />}
        </div>
    </div>
)

export const Checkout = (props: CheckoutProps): JSX.Element => {
    const className = classNames('checkout', `h-theme-${props.theme}`, {
        'has-floorplan': props.hasFloorplan,
        'is-free': props.isFree,
        'is-loading h-loading': props.isLoading,
    })

    const buttonKeySuffix: string =
        !props.activeCheckoutStep || props.activeCheckoutStep === CheckoutStep.TICKETS
            ? 'tickets'
            : 'products/summary'

    return (
        <div class={className} data-interval={props.interval}>
            {renderMobileHeader(props)}
            <div class="h-container">
                <div class="checkout__grid">
                    <div class="checkout__grid-col checkout__grid-col--side">
                        {renderSidebar(props)}
                    </div>
                    <div class="checkout__grid-col checkout__grid-col--main">
                        <Loader isHidden={!props.isLoading} />
                        {props.progressBar && <ProgressBarSection {...props.progressBar} />}
                        {props.isProductSaleEnabled &&
                            props.activeCheckoutStep === CheckoutStep.SUMMARY &&
                            props.paySectionText && (
                                <div class="checkout__paySectionLabel">{props.paySectionText}</div>
                            )}
                        {props.error && (
                            <PaymentError class="checkout__payment-error" {...props.error} />
                        )}
                        {props.eventSelectionSection && (
                            <EventSelectionSection {...props.eventSelectionSection} />
                        )}
                        {props.floorplanSection && <FloorplanSection {...props.floorplanSection} />}
                        {props.introSection && <IntroSection {...props.introSection} />}
                        {props.productFiltersSection && (
                            <ProductFiltersSection {...props.productFiltersSection} />
                        )}
                        {props.isProductSaleEnabled &&
                            props.activeCheckoutStep === CheckoutStep.PRODUCTS &&
                            props.productSelectionText && (
                                <div class="checkout__specialProductsSelectionLabel">
                                    {props.productSelectionText}
                                </div>
                            )}
                        {props.productsListSection && (
                            <ProductsListSection {...props.productsListSection} />
                        )}
                        {props.ticketsSection && <TicketsSection {...props.ticketsSection} />}
                        {props.cardsSection && <CardsSection {...props.cardsSection} />}
                        {props.contactSection && <ContactSection {...props.contactSection} />}
                        {props.paymentSection && <PaymentSection {...props.paymentSection} />}
                        {props.text && (
                            <div class="checkout__section">
                                <div class="checkout__section-text">{props.text}</div>
                            </div>
                        )}
                        {props.buttons.length > 0 && (
                            <div class="checkout__footer">
                                {props.terms && (
                                    <a class="checkout__section-text" href={props.terms.link}>
                                        {props.terms.label}
                                    </a>
                                )}
                                <div class="checkout__section-content checkout__buttons">
                                    {props.buttons.map((button, index) => (
                                        <Button key={index + buttonKeySuffix} {...button} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
